import { supabase } from "@/integrations/supabase/client";

export interface ResolveUserPlanCodeResult {
  /** Código do plano efetivo, ou null para cair no fallback do profile (ex.: pending). */
  planCode: string | null;
  isPendingPayment: boolean;
}

/**
 * Resolve o plano vigente: ativo/futuro; cancelado com acesso até next_billing_date; ou fallback ao profile.
 */
export async function resolveUserPlanCode(
  userId: string,
): Promise<ResolveUserPlanCodeResult> {
  const todayStr = new Date().toISOString().split("T")[0];

  const { data: activeSub } = await supabase
    .from("subscriptions")
    .select("plan_code, status")
    .eq("user_id", userId)
    .in("status", ["active", "future", "pending"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  const isPendingPayment = activeSub?.status === "pending";
  if (isPendingPayment) {
    return { planCode: null, isPendingPayment: true };
  }

  if (activeSub?.plan_code) {
    return { planCode: activeSub.plan_code, isPendingPayment: false };
  }

  const { data: canceledWithPeriod } = await supabase
    .from("subscriptions")
    .select("plan_code, next_billing_date")
    .eq("user_id", userId)
    .eq("status", "canceled")
    .not("next_billing_date", "is", null)
    .gt("next_billing_date", todayStr)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (canceledWithPeriod?.plan_code) {
    return { planCode: canceledWithPeriod.plan_code, isPendingPayment: false };
  }

  return { planCode: null, isPendingPayment: false };
}

/** Há assinatura (incl. cancelada com período pago) que sustenta este plan_code no perfil? */
export async function profilePlanCodeIsStillValid(
  userId: string,
  planCode: string,
): Promise<boolean> {
  const todayStr = new Date().toISOString().split("T")[0];
  const { data } = await supabase
    .from("subscriptions")
    .select("status, next_billing_date")
    .eq("user_id", userId)
    .eq("plan_code", planCode)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!data) return false;
  if (data.status !== "canceled") return true;
  if (!data.next_billing_date) return false;
  return data.next_billing_date > todayStr;
}
